import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {BrowserRouter as Router, Redirect, Route, Switch} from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "styles/css/App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import ErrorBoundary from "components/Common/ErrorBoundary";
import {GlobalStyle} from "components/Common/GlobalStyle";
import AppContexts from "contexts/AppContexts";
import ImportBridgeListener from "components/Common/ImportBridgeListener";
import PrivateRoute from "routes/PrivateRoute";
import AdminRoute from "routes/AdminRoute";
import PublicRoute from "routes/PublicRoute";
import ToastService, {toast, ToastContainer} from "services/ToastService.js";
import Layout from "components/Common/Layout";
import {PAGE_URLS} from "constants/AppConstants.js";
import OnboardingPage from "pages/OnboardingPage";
import RootPage from "pages/RootPage";
import {NavigationsProvider} from "contexts/NavigationsContext";
import {WebPaywallProvider} from "contexts/WebPaywallContext";
import {AnalyticsTrackerPlugin, isAndroid} from "FirebaseUtils";
import BrowserService from "services/BrowserService.js";
import AppUrlListener from "AppUrlListener";
import {isNil} from "lodash";
import "lib/mapkit";
import {Network} from "@capacitor/network";
import LogService from "services/LogService";
import {App as CapApp} from "@capacitor/app";
import {Device} from "@capacitor/device";
import {ThemeProvider} from "@mui/material";
import {getMuiTheme} from "styles/muiTheme";
import useColorScheme from "hooks/useColorScheme";
import ColorSchemeUrlSync from "components/Common/ColorSchemeUrlSync";
import TokenService from "services/TokenService.js";
import {useCallbackRef} from "use-callback-ref";
import {registerPlugin} from "@capacitor/core";
import {useTranslation} from "react-i18next";
import SignInPage from "pages/SignInPage";

// Pages load on demand, so the first screen (sign-in, onboarding) does not download the whole app.
// After a deploy, a tab opened before it asks for chunk files that no longer exist: reload once to get
// the new build instead of failing (the flag stops a reload loop if the chunk is missing for good;
// offline, a reload would only show the browser's offline page).
const CHUNK_RELOAD_KEY = "ws-chunk-reload";
const lazyPage = (load: () => Promise<{default: React.ComponentType<any>}>) =>
  React.lazy(() =>
    load()
      .then((module) => {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        return module;
      })
      .catch((error) => {
        if (navigator.onLine && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
          window.location.reload();
          return new Promise<never>(() => undefined);
        }
        throw error;
      })
  );
const ActivityPage = lazyPage(() => import("pages/ActivityPage"));
const CompareActivitiesPage = lazyPage(() => import("pages/CompareActivitiesPage"));
const AdminPage = lazyPage(() => import("pages/AdminPage"));
const ChallengesPage = lazyPage(() => import("pages/ChallengesPage"));
const DiaryPage = lazyPage(() => import("pages/DiaryPage"));
const EquipmentsPage = lazyPage(() => import("pages/EquipmentsPage"));
const ForgotPasswordPage = lazyPage(() => import("pages/ForgotPasswordPage"));
const LivePage = lazyPage(() => import("pages/LivePage"));
const MyAccountPage = lazyPage(() => import("pages/MyAccountPage"));
const StatisticsPage = lazyPage(() => import("pages/StatisticsPage"));
const SubscriptionExpirePage = lazyPage(() => import("pages/SubscriptionExpirePage"));
const TrashPage = lazyPage(() => import("pages/TrashPage"));
const UnAuthorizedPage = lazyPage(() => import("pages/UnAuthorizedPage"));
const RefusedActivitiesPage = lazyPage(() => import("pages/RefusedActivitiesPage"));
const InvalidUrlPage = lazyPage(() => import("pages/InvalidUrlPage"));
const AdminDiaryPage = lazyPage(() => import("pages/AdminDiaryPage"));
const AdminEquipmentsPage = lazyPage(() => import("pages/AdminEquipmentsPage"));
const AdminDashboardPage = lazyPage(() => import("pages/AdminDashboardPage"));
const AroundMePage = lazyPage(() => import("pages/AroundMePage"));
const RedirectIOSApp = lazyPage(() => import("pages/RedirectApp"));
const AdminChallengesPage = lazyPage(() => import("pages/AdminChallengesPage"));
const AndroidSettingsPage = lazyPage(() => import("pages/AndroidSettingsPage"));
const RedeemCodePage = lazyPage(() => import("pages/RedeemCodePage"));
const ConnectServicePage = lazyPage(() => import("pages/ConnectServicePage"));
const AdminRedeemCodePage = lazyPage(() => import("pages/AdminRedeemCodePage"));
const AdminLocalExpertPage = lazyPage(() => import("pages/AdminLocalExpertPage"));
const AdminActivitiesPage = lazyPage(() => import("pages/AdminActivitiesPage"));
const AdminAutoTrimPage = lazyPage(() => import("pages/AdminAutoTrimPage"));
const FavoritesPage = lazyPage(() => import("pages/FavoritesPage"));
const ActivityStoreRedirectPage = lazyPage(() => import("pages/ActivityStoreRedirectPage"));

toast.configure();

const TabBarPlugin: any = registerPlugin("TabBarPlugin");

// Every page rendered inside <Layout>; any other URL falls through to the not-found page.
const layoutRoutePaths = isAndroid
  ? ["/"]
  : [
      PAGE_URLS.statisticsPageUrl,
      PAGE_URLS.diaryPageUrl,
      `${PAGE_URLS.compareActivitiesPageUrl}/:activityIdA/:activityIdB`,
      PAGE_URLS.trashPageUrl,
      PAGE_URLS.refusedActivitiesPageUrl,
      PAGE_URLS.myAccountPageUrl,
      PAGE_URLS.challengesPageUrl,
      PAGE_URLS.equipmentsPageUrl,
      `${PAGE_URLS.activityPageUrl}/:rest*`,
      "/users/:userId/activities/:activityId",
      `${PAGE_URLS.livePageUrl}/:rest*`,
      PAGE_URLS.redeemCode,
      PAGE_URLS.androidSettingsPageUrl,
      PAGE_URLS.favoritesPageUrl,
      PAGE_URLS.aroundMePageUrl
    ];

function App() {
  const {t} = useTranslation();
  const [promisesUpdated, setPromisesUpdated] = useState(0);
  const [networkStatus, setNetworkStatus] = useState(false);
  const promisedSyncRef = useRef<any[]>([]);
  const isDiaryActive = useCallbackRef(false, () => setPromisesUpdated((prev) => prev + 1));

  // A stable setter so DiaryPage's mount effect doesn't see a new function reference
  // on every App re-render, which previously caused a render → setIsDiaryActive(true)
  // → setPromisesUpdated → render → cleanup setIsDiaryActive(false) → ...
  // infinite loop that locked the UI thread and prevented the cloud sync debounce
  // timer from ever firing.
  const setIsDiaryActive = useCallback(
    (isActive: boolean) => {
      isDiaryActive.current = isActive;
    },
    [isDiaryActive]
  );
  const diaryRouteProps = useMemo(() => ({setIsDiaryActive}), [setIsDiaryActive]);

  useEffect(() => {
    if (!isDiaryActive.current) {
      cancelAllPromises();
      console.log("isDiaryActive Cancelling all sync promises");
    }
  }, [isDiaryActive.current, promisesUpdated]);

  // Native Diary fully replaced the web DiaryPage, so its mount effect (the only other
  // place setIsDiaryActive is called) never runs anymore while the user is on that tab.
  // MainActivity.showTab notifies this on every tab switch (cold start, deep link, tab
  // tap) so the cloud sync gated behind isDiaryActive keeps running for still-web pages
  // (Trash, Statistics, ...) that read from the same local mirror.
  useEffect(() => {
    if (!isAndroid) return;

    let listener: any;
    (async () => {
      listener = await TabBarPlugin.addListener("diaryActive", (data: any) => {
        setIsDiaryActive(data.isActive === true);
      });
    })();

    return () => {
      listener?.remove();
    };
  }, [setIsDiaryActive]);

  useEffect(() => {
    if (isAndroid) {
      // Check internet connection
      let listener: any;
      (async () => {
        const appInfo = await CapApp.getInfo();
        LogService.addInfoLogAsync(null, `App info: ${JSON.stringify(appInfo)}`, false, true);

        listener = await Network.addListener("networkStatusChange", hasInternet);
        const status = await Network.getStatus();
        hasInternet(status);

        await CapApp.addListener("appStateChange", stateChanged);
        await CapApp.addListener("pause", stateChanged);
      })();

      // We track the device info periodically
      const intervalId = setInterval(async () => {
        const deviceInfo = await Device.getInfo();
        LogService.addInfoLogAsync(null, `Device info: ${JSON.stringify(deviceInfo)}`, false, true);
      }, 1000 * 300);

      return (async () => {
        await listener?.remove();
        clearInterval(intervalId);
      }) as unknown as () => void;
    }
  }, []);

  const cancelAllPromises = () => {
    promisedSyncRef.current?.forEach((promise) => {
      promise?.cancel?.();
    });
    promisedSyncRef.current = [];
  };

  const hasInternet = (status: any) => {
    if (!status.connected) {
      setNetworkStatus(false);
    } else {
      setNetworkStatus(true);
      initMapkit();
    }
  };

  const stateChanged = (state: any = null) => {
    if (!state) {
      LogService.addInfoLogAsync(null, "App is in background", false, true);
      return;
    }

    if (state.isActive) {
      LogService.addInfoLogAsync(null, "App is foreground", false, true);
    } else {
      LogService.addInfoLogAsync(null, "App is in background", false, true);
    }
  };

  useEffect(() => {
    if (!isAndroid && !BrowserService.isWebview()) {
      AnalyticsTrackerPlugin.trackEvent({
        eventName: "Webapp Opened",
        properties: {source: "webapp"}
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    initMapkit();

    if (!isAndroid) {
      window.addEventListener("offline", showToastOffline);
    }

    return () => {
      try {
        if (!isNil(mapkit)) {
          mapkit.removeEventListener("error", mapkitInitErrorCallback);
        }
      } catch (error) {
        console.error("Error removing mapkit event listener", error);
      }

      if (!isAndroid) {
        window.removeEventListener("offline", showToastOffline);
      }
    };
  }, []);

  const mapkitInitErrorCallback = (error: any) => {
    LogService.addErrorLogAsync(null, `mapkit-js init failed reason`, error);
  };

  const initMapkit = () => {
    try {
      if (!isNil(mapkit)) {
        mapkit.init({
          authorizationCallback: async (done: any) => {
            try {
              const mapkitJsToken = await TokenService.fetchMapkitJsToken(!isAndroid);
              done(mapkitJsToken);
            } catch (error) {
              console.error("Error fetching MapKit token", error);
            }
          }
        });

        mapkit.addEventListener("error", mapkitInitErrorCallback);
      }
    } catch (error) {
      console.error("Error initializing mapkit", error);
    }
  };

  const colorScheme = useColorScheme();
  const muiTheme = useMemo(() => getMuiTheme(colorScheme), [colorScheme]);

  const showToastOffline = (event: any) => {
    ToastService.info(t("explore.noInternetConnectionShort"), "top-center", false as any);
  };

  return (
    <>
      <ErrorBoundary>
        <GlobalStyle />
        <ThemeProvider theme={muiTheme}>
          <AppContexts>
            <Router>
              <ColorSchemeUrlSync />
              {isAndroid ? <AppUrlListener /> : null}
              <WebPaywallProvider>
                <React.Suspense fallback={null}>
                  <Switch>
                    <PublicRoute path={PAGE_URLS.publicRedirectApp} component={RedirectIOSApp} />
                    <PublicRoute exact path={PAGE_URLS.connectServicePageUrl} component={ConnectServicePage} />
                    <PublicRoute
                      exact
                      path={PAGE_URLS.publicChallengeLeaderboardPageUrl}
                      component={ChallengesPage}
                      props={{leaderboardIframe: true}}
                    />
                    <PublicRoute
                      path={PAGE_URLS.publicAroundMePageUrl}
                      component={AroundMePage}
                      props={{isWebview: true}}
                    />
                    <PublicRoute
                      path={PAGE_URLS.publicNavigationPageUrl}
                      component={ActivityPage}
                      props={{isWebview: true}}
                    />
                    <PublicRoute
                      exact={!isAndroid}
                      path={isAndroid ? PAGE_URLS.signInPageUrl : "/"}
                      component={isAndroid ? SignInPage : RootPage}
                    />
                    <PublicRoute exact path={PAGE_URLS.signInPageUrl} component={SignInPage} />
                    {/* Legacy /sign-up now lives in the onboarding wizard */}
                    <Redirect exact from={PAGE_URLS.signUpPageUrl} to={PAGE_URLS.onboardingPageUrl} />
                    <PublicRoute exact path={PAGE_URLS.onboardingPageUrl} component={OnboardingPage} />
                    {/* Payment links: the onboarding opens on the link's offer */}
                    <Route
                      exact
                      path={PAGE_URLS.payPageUrl}
                      render={({location}) => (
                        <Redirect to={{pathname: PAGE_URLS.onboardingPageUrl, search: location.search}} />
                      )}
                    />

                    <PublicRoute exact path={PAGE_URLS.forgetPasswordPageUrl} component={ForgotPasswordPage} />
                    <PublicRoute exact path={PAGE_URLS.unauthorizedPageUrl} component={UnAuthorizedPage} />
                    <PublicRoute path={PAGE_URLS.subscriptionExpirePageUrl} component={SubscriptionExpirePage} />

                    <AdminRoute
                      exact
                      path={PAGE_URLS.adminPageUrl}
                      component={AdminDashboardPage}
                      props={{userNeeded: false}}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}${PAGE_URLS.usersPageUrl}`}
                      component={AdminPage}
                      props={{userNeeded: false}}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}/:userId${PAGE_URLS.diaryPageUrl}`}
                      component={AdminDiaryPage}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}/:userId${PAGE_URLS.activityPageUrl}/:activityId/map/`}
                      component={ActivityPage}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}/:userId${PAGE_URLS.activityPageUrl}/:activityId/share/`}
                      component={ActivityPage}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}/:userId${PAGE_URLS.activityPageUrl}/:activityId/map`}
                      component={ActivityPage}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}/:userId${PAGE_URLS.activityPageUrl}/:activityId/share`}
                      component={ActivityPage}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}/:userId${PAGE_URLS.activityPageUrl}/:activityId`}
                      component={ActivityPage}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}/:userId${PAGE_URLS.compareActivitiesPageUrl}/:activityIdA/:activityIdB`}
                      component={CompareActivitiesPage}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}${PAGE_URLS.equipmentsPageUrl}`}
                      component={AdminEquipmentsPage}
                      props={{userNeeded: false}}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}${PAGE_URLS.challengesPageUrl}`}
                      component={AdminChallengesPage}
                      props={{userNeeded: false}}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}${PAGE_URLS.adminActivitiesPageUrl}`}
                      component={AdminActivitiesPage}
                      props={{userNeeded: false}}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}${PAGE_URLS.autoTrimPageUrl}`}
                      component={AdminAutoTrimPage}
                      props={{userNeeded: false}}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}${PAGE_URLS.redeemCode}`}
                      component={AdminRedeemCodePage}
                      props={{userNeeded: false}}
                    />
                    <AdminRoute
                      exact
                      path={`${PAGE_URLS.adminPageUrl}${PAGE_URLS.localExpertsPageUrl}`}
                      component={AdminLocalExpertPage}
                      props={{userNeeded: false}}
                    />

                    <Redirect exact from={PAGE_URLS.oldDiaryPageUrl} to={PAGE_URLS.diaryPageUrl} />

                    <Route exact={!isAndroid} path={layoutRoutePaths}>
                      <Layout>
                        <React.Suspense fallback={null}>
                          <NavigationsProvider
                            networkStatus={networkStatus}
                            diaryActiveTick={promisesUpdated}
                            addPromisedSyncRef={(promise) => {
                              promisedSyncRef.current.push(promise);
                            }}
                            cancelAllPromises={cancelAllPromises}
                            isDiaryActive={isDiaryActive}
                            userId={undefined}
                          >
                            <ImportBridgeListener />
                            <PrivateRoute exact path={PAGE_URLS.statisticsPageUrl} component={StatisticsPage} />
                            <PrivateRoute
                              exact
                              path={PAGE_URLS.diaryPageUrl}
                              props={diaryRouteProps}
                              component={DiaryPage}
                            />
                            <PrivateRoute
                              exact
                              path={`${PAGE_URLS.compareActivitiesPageUrl}/:activityIdA/:activityIdB`}
                              component={CompareActivitiesPage}
                            />
                            <PrivateRoute exact path={PAGE_URLS.trashPageUrl} component={TrashPage} />
                            <PrivateRoute
                              exact
                              path={PAGE_URLS.refusedActivitiesPageUrl}
                              component={RefusedActivitiesPage}
                            />
                            <PrivateRoute exact path={PAGE_URLS.myAccountPageUrl} component={MyAccountPage} />
                          </NavigationsProvider>

                          <PrivateRoute exact path={PAGE_URLS.challengesPageUrl} component={ChallengesPage} />
                          <PrivateRoute exact path={PAGE_URLS.equipmentsPageUrl} component={EquipmentsPage} />
                          {isAndroid && (
                            <>
                              <PublicRoute
                                exact={true}
                                path={"/"}
                                component={AroundMePage}
                                props={{isWebview: false}}
                              />
                              <PublicRoute
                                exact
                                path={PAGE_URLS.aroundMePageUrl}
                                component={AroundMePage}
                                props={{isWebview: false}}
                              />
                            </>
                          )}
                          <PublicRoute path={PAGE_URLS.activityPageUrl} component={ActivityPage} />
                          <PublicRoute
                            path="/users/:userId/activities/:activityId"
                            component={ActivityStoreRedirectPage}
                          />
                          <PublicRoute path={PAGE_URLS.livePageUrl} component={LivePage} />
                          <PrivateRoute exact path={PAGE_URLS.redeemCode} component={RedeemCodePage} />
                          <PrivateRoute exact path={PAGE_URLS.androidSettingsPageUrl} component={AndroidSettingsPage} />
                          <PrivateRoute exact path={PAGE_URLS.favoritesPageUrl} component={FavoritesPage} />
                          {!isAndroid && (
                            <PrivateRoute
                              exact
                              path={PAGE_URLS.aroundMePageUrl}
                              component={AroundMePage}
                              props={{isWebview: false}}
                            />
                          )}
                        </React.Suspense>
                      </Layout>
                    </Route>

                    <PublicRoute path="*" component={InvalidUrlPage} />
                  </Switch>
                </React.Suspense>
              </WebPaywallProvider>
            </Router>
          </AppContexts>
          <ToastContainer />
        </ThemeProvider>
      </ErrorBoundary>
    </>
  );
}

export default App;
